import { createFinalisedVersion, exportAvailability, parseTrialStatus } from '@suvoda-uat/domain';
import type {
  ProjectExportAvailability,
  ProjectPrimaryAction,
  ProjectRow,
} from '@suvoda-uat/ui-suvoda';
import type { Trial } from './api-client.js';

/**
 * Map a contract `Trial` to a Dashboard `ProjectCard` row (T053). The workflow
 * status stays on the canonical five-stage lifecycle model. Only a finalised
 * Trial offers Preview, the rest Continue. `updatedLabel` shows the calendar
 * date; relative "n days ago" is the full Dashboard's job (D2/D3).
 */

const toExportAvailability = (trial: Trial): ProjectExportAvailability => {
  const status = parseTrialStatus(trial.status);
  if (status === 'finalised' && trial.latestFinalisedVersion === undefined) {
    return { kind: 'absent' };
  }

  const availability = exportAvailability(status, {
    ready: trial.reviewProgress?.ready ?? 0,
    total: trial.reviewProgress?.total ?? 0,
    packComplete: false,
    latestFinalisedVersion:
      trial.latestFinalisedVersion === undefined
        ? null
        : createFinalisedVersion(trial.latestFinalisedVersion.version),
  });
  if (availability.kind === 'absent') return { kind: 'absent' };
  if (availability.kind === 'active') return { kind: 'active', version: availability.version };
  if (availability.reason === 'outstanding') {
    return { kind: 'unavailable', reason: `${availability.outstanding} Test Cases outstanding` };
  }
  if (availability.reason === 'changesSinceVersion') {
    return { kind: 'unavailable', reason: `changes since ${availability.version}` };
  }
  return { kind: 'unavailable', reason: 'finalise to export' };
};

const formatBuildLabel = (label: Trial['buildLabel']): string => {
  if ('ccf' in label) return `CCF-${String(label.ccf.number).padStart(3, '0')}`;
  if ('amendment' in label) return `Amendment ${label.amendment.number}`;
  return 'Initial build';
};

export const toProjectRow = (trial: Trial, viewerUserId?: string): ProjectRow => {
  const status = parseTrialStatus(trial.status);
  const primaryAction: ProjectPrimaryAction = status === 'finalised' ? 'preview' : 'continue';
  return {
    id: trial.id,
    title: trial.name,
    // "Mine" is a client-side filter over the team-wide ListTrials response
    // (F-019 FR-005) — owner.id only; the creator of a since-transferred
    // trial is NOT "mine".
    mine: viewerUserId !== undefined && trial.owner.id === viewerUserId,
    status,
    primaryAction,
    buildLabel: formatBuildLabel(trial.buildLabel),
    // F-139: the full UserSummary (id/name/initials) so the card can render the
    // owner picker; "My Projects" still keys off owner.id (above).
    owner: trial.owner,
    reviewer: trial.reviewer?.name ?? '—',
    createdBy: trial.createdBy.name,
    updatedLabel: `updated ${trial.updatedAt.slice(0, 10)}`,
    ...(trial.reviewProgress === undefined ? {} : { reviewProgress: trial.reviewProgress }),
    ...(trial.latestFinalisedVersion === undefined
      ? {}
      : { latestFinalisedVersion: trial.latestFinalisedVersion.version }),
    // F-033 (R5): the provisional-name cue (absent ⇒ false on legacy trials).
    needsNameConfirmation: trial.needsNameConfirmation ?? false,
    exportAvailability: toExportAvailability(trial),
  };
};
